'use client';

import React from 'react';
import type { AiSearchSuggestion } from '@repo/api';

const ROTATE_INTERVAL_MS = 3000;

/**
 * Cycles the AI bar's placeholder through the default prompt and the starter
 * suggestions (default first), one every three seconds, looping.
 *
 * Pass `enabled: false` when the placeholder isn't visible (the field has text)
 * so an off-screen string doesn't re-render the bar every second.
 */
export function useRotatingPlaceholder(
  defaultPlaceholder: string,
  suggestions: AiSearchSuggestion[],
  enabled: boolean,
): string {
  const phrases = React.useMemo(
    () => [defaultPlaceholder, ...suggestions.map(s => s.text).filter(Boolean)],
    [defaultPlaceholder, suggestions],
  );

  const [index, setIndex] = React.useState(0);

  // Suggestions land after the first render (and the list can change length),
  // so restart the cycle rather than leaving the index pointing past the end.
  React.useEffect(() => {
    setIndex(0);
  }, [phrases]);

  React.useEffect(() => {
    if (!enabled || phrases.length < 2) return;
    const id = window.setInterval(() => {
      setIndex(i => (i + 1) % phrases.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, phrases]);

  return phrases[index] ?? defaultPlaceholder;
}

export default useRotatingPlaceholder;
